import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, Clapperboard, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { primeClip, renderSocialClip } from "../../lib/socialClip";

export default function SocialClipDialog({ review, open, onOpenChange, call, base }) {
  const [endText, setEndText] = useState("");
  const [stage, setStage] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [clip, setClip] = useState(null);

  useEffect(() => {
    if (!open) return;
    call("/admin/video-reviews-settings").then((s) => setEndText((t) => t || s.end_card_text)).catch(() => {});
  }, [open, call]);
  useEffect(() => () => clip && URL.revokeObjectURL(clip.url), [clip]);

  const make = async () => {
    const prime = primeClip(`${base}/api/video-reviews/${review.token}/video?key=${review.preview_key}`);
    try {
      setClip(null); setStage("captions");
      const saved = await call("/admin/video-reviews-settings", { method: "PUT", body: JSON.stringify({ end_card_text: endText }) });
      setEndText(saved.end_card_text);
      const { segments } = await call(`/admin/video-reviews/${review.token}/captions`, { method: "POST" });
      setStage("render"); setProgress(0);
      const blob = await renderSocialClip({ prime, review, segments, endText: saved.end_card_text, onProgress: setProgress });
      setClip({ url: URL.createObjectURL(blob), ext: blob.type.includes("mp4") ? "mp4" : "webm" });
      setStage("done");
    } catch (e) { prime.actx.close().catch(() => {}); toast.error(e.message || "Couldn't make the clip"); setStage("idle"); }
  };

  const busy = stage === "captions" || stage === "render";
  const name = (review.first_name || review.client_name || "review").toLowerCase().replace(/\W+/g, "-");

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="social-clip-dialog">
        <DialogHeader><DialogTitle className="font-display">Social clip — {review.first_name || review.client_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-ink-secondary">A 1080×1920 vertical video with your logo, captions, name, stars and trip, ending on a branded card. Ready for Instagram Reels, Stories and TikTok.</p>
        <label className="block">
          <span className="font-mono text-[11px] uppercase tracking-widest text-ink-muted">End card text</span>
          <input value={endText} onChange={(e) => setEndText(e.target.value)} maxLength={160} disabled={busy} data-testid="clip-end-text"
            className="mt-1.5 w-full h-10 px-3 rounded-sm border border-sand-dark text-sm focus:outline-none focus:border-moss" />
        </label>
        {busy && (
          <div data-testid="clip-progress">
            <div className="flex items-center gap-2 text-sm text-ink-secondary mb-2"><Loader2 className="w-4 h-4 animate-spin text-terracotta" />
              {stage === "captions" ? "Writing captions…" : `Rendering clip… ${Math.round(progress * 100)}% — keep this tab open`}</div>
            <div className="h-1.5 bg-sand rounded-full overflow-hidden"><div className="h-full bg-terracotta transition-[width]" style={{ width: `${progress * 100}%` }} /></div>
          </div>
        )}
        {clip && (
          <div className="flex gap-4 items-end">
            <video src={clip.url} controls playsInline className="w-40 aspect-[9/16] rounded-md bg-black" data-testid="clip-preview" />
            <div className="flex flex-col gap-2">
              <a href={clip.url} download={`${name}-ra-review.${clip.ext}`} data-testid="clip-download"
                className="h-10 px-4 rounded-sm bg-moss text-white text-sm font-semibold flex items-center gap-2"><Download className="w-4 h-4" /> Download .{clip.ext}</a>
              {clip.ext === "webm" && <p className="text-xs text-ink-muted max-w-[200px]">This browser saved WebM. For Instagram, make the clip in Chrome (latest) or Safari to get MP4.</p>}
            </div>
          </div>
        )}
        {!busy && (
          <button onClick={make} data-testid="clip-make"
            className="h-11 rounded-sm bg-terracotta text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-terracotta-hover">
            {clip ? <><RefreshCw className="w-4 h-4" /> Make again</> : <><Clapperboard className="w-4 h-4" /> Make social clip</>}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
